/**
 * Features Section Scroll Animation with GSAP ScrollTrigger
 * Handles sticky scroll effect with sequential block appearance and blue progress line animation
 */

(function() {
	'use strict';

	/**
	 * Initializing animation after loading GSAP
	 */
	let initAttempts = 0;
	const maxInitAttempts = 50; // Maximum 5 seconds wait (50 *100ms)

	function initFeaturesAnimation() {
		// Checking for GSAP
		if (typeof gsap === 'undefined') {
			initAttempts++;
			if (initAttempts < maxInitAttempts) {
				// Try again after 100ms
				setTimeout(initFeaturesAnimation, 100);
			} else {
				console.error('GSAP failed to load after multiple attempts');
			}
			return;
		}

		// Checking for the presence of ScrollTrigger in GSAP
		// ScrollTrigger can be accessed through various paths
		let ScrollTriggerPlugin = null;

		// First we check gsap.ScrollTrigger (the most common method)
		if (gsap.ScrollTrigger) {
			ScrollTriggerPlugin = gsap.ScrollTrigger;
		}
		// Then we check window.ScrollTrigger
		else if (typeof window !== 'undefined' && window.ScrollTrigger) {
			ScrollTriggerPlugin = window.ScrollTrigger;
			gsap.registerPlugin(ScrollTriggerPlugin);
		}
		// Then we check the global ScrollTrigger
		else if (typeof ScrollTrigger !== 'undefined') {
			ScrollTriggerPlugin = ScrollTrigger;
			gsap.registerPlugin(ScrollTriggerPlugin);
		}
		// Checking GSAP plugins
		else if (gsap.plugins && gsap.plugins.ScrollTrigger) {
			ScrollTriggerPlugin = gsap.plugins.ScrollTrigger;
		}
		// If not found, try again
		else {
			initAttempts++;
			if (initAttempts < maxInitAttempts) {
				// Try again after 100ms
				setTimeout(initFeaturesAnimation, 100);
			} else {
				console.error('ScrollTrigger plugin not found in GSAP after', maxInitAttempts, 'attempts. Make sure ScrollTrigger is included in gsap.min.js');
			}
			return;
		}

		// Checking that ScrollTrigger is really functional
		if (!ScrollTriggerPlugin || (typeof ScrollTriggerPlugin.create !== 'function' && typeof ScrollTriggerPlugin !== 'function')) {
			initAttempts++;
			if (initAttempts < maxInitAttempts) {
				setTimeout(initFeaturesAnimation, 100);
			} else {
				console.error('ScrollTrigger found but not functional. Expected create method or function.');
			}
			return;
		}

		const featuresSection = document.getElementById('features-section');
		if (!featuresSection) {
			return;
		}

		const headerEl = featuresSection.querySelector('.features-section__header');
		const blockWrappers = featuresSection.querySelectorAll('.features-section__block-wrapper');
		const blocks = featuresSection.querySelectorAll('.features-section__block');
		const progressLine = featuresSection.querySelector('.features-section__progress-line');
		const blocksWrapper = featuresSection.querySelector('.features-section__blocks-wrapper');

		if (blocks.length === 0 || !progressLine || !blocksWrapper) {
			return;
		}

		// Delay before blocks start appearing.
		const headerProgressThreshold = 0.25;
		const headerTypeStart = 0;
		const headerTypeEnd = headerProgressThreshold;

		// Split text into char spans for type/untype; wrap each word in nowrap span so words never break mid-word (e.g. "helped" stays together)
		function splitTextIntoChars(container) {
			if (!container || !container.childNodes.length) return [];
			const chars = [];
			function createCharSpan(ch) {
				const span = document.createElement('span');
				span.className = 'features-section__char';
				span.style.display = 'inline-block';
				span.textContent = ch === ' ' ? '\u00A0' : ch;
				chars.push(span);
				return span;
			}
			function processTextNode(text, parent, beforeNode) {
				if (!text || text.length === 0) return;
				const words = text.split(/(\s+)/);
				for (let i = 0; i < words.length; i++) {
					const token = words[i];
					let toInsert;
					if (/^\s+$/.test(token)) {
						for (let j = 0; j < token.length; j++) {
							const ch = token[j];
							if (ch !== '\n' && ch !== '\r' && ch !== '\t') {
								toInsert = createCharSpan(ch);
								parent.insertBefore(toInsert, beforeNode);
							}
						}
					} else if (token.length > 0) {
						const wordWrap = document.createElement('span');
						wordWrap.className = 'features-section__word';
						wordWrap.style.whiteSpace = 'nowrap';
						wordWrap.style.display = 'inline-block';
						for (let j = 0; j < token.length; j++) {
							wordWrap.appendChild(createCharSpan(token[j]));
						}
						parent.insertBefore(wordWrap, beforeNode);
					}
				}
			}
			function walk(node) {
				if (node.nodeType === Node.TEXT_NODE) {
					const text = node.textContent || '';
					const parent = node.parentNode;
					processTextNode(text, parent, node);
					parent.removeChild(node);
				} else if (node.nodeType === Node.ELEMENT_NODE && (!node.classList || !node.classList.contains('features-section__char'))) {
					Array.from(node.childNodes).forEach(walk);
				}
			}
			Array.from(container.childNodes).forEach(walk);
			return chars;
		}

		function getCharsFromSelector(parent, selector) {
			const el = parent && parent.querySelector(selector);
			if (!el) return [];
			return splitTextIntoChars(el);
		}

		const headerDescChars = getCharsFromSelector(headerEl, '.features-section__description');
		const headerTrailingChars = getCharsFromSelector(headerEl, '.features-section__trailing-phrase');
		const headerEvoChars = getCharsFromSelector(headerEl, '.features-section__header-evo');
		const headerOnaChars = getCharsFromSelector(headerEl, '.features-section__header-ona');
		const headerChars = headerDescChars
			.concat(headerTrailingChars)
			.concat(headerEvoChars)
			.concat(headerOnaChars);

		function applyTypeProgress(chars, progress) {
			const n = chars.length;
			if (n === 0) return;
			chars.forEach(function (ch, i) {
				const visible = progress >= (i + 1) / n;
				gsap.set(ch, { opacity: visible ? 1 : 0, y: visible ? 0 : 8 });
			});
		}

		function applyHeaderTypeProgress(progress) {
			const headerProgress = progress <= headerTypeStart
				? 0
				: progress >= headerTypeEnd
					? 1
					: (progress - headerTypeStart) / (headerTypeEnd - headerTypeStart);
			applyTypeProgress(headerChars, headerProgress);
		}

		const totalBlocks = blocks.length;
		const viewportHeight = window.innerHeight;

		/**
		 * Checks whether the current viewport is mobile.
		 */
		function isMobile() {
			return window.innerWidth <= 767;
		}

		// Initialize blocks: gradient reveal animation for all devices
		// Blocks stay in place, gradient reveals from left to right
		blocks.forEach(function(block) {
			const title = block.querySelector('.features-section__block-title');
			const description = block.querySelector('.features-section__block-description');

			// Set initial state: block visible, text hidden via gradient
			gsap.set(block, {
				opacity: 1
			});

			if (title) {
				// Add gradient class and set initial gradient stop to 0%
				title.classList.add('gradient-reveal');
				title.style.setProperty('--gradient-stop', '0%');
			}

			if (description) {
				// Add gradient class and set initial gradient stop to 0%
				description.classList.add('gradient-reveal');
				description.style.setProperty('--gradient-stop', '0%');
			}
		});

		// Hiding the progress line initially
		// Set appropriate initial styles based on device
		if (isMobile()) {
			// Mobile: start from top (0) with 0 height
			gsap.set(progressLine, {
				opacity: 0,
				scaleX: 0,
				scaleY: 0,
				top: '0px',
				height: '0px',
				left: '0px',
				width: '2px'
			});
		} else {
			// Desktop: ensure mobile styles are reset
			gsap.set(progressLine, {
				opacity: 0,
				scaleX: 0,
				scaleY: 0,
				top: 'auto',
				height: '2px',
				bottom: '-24px'
			});
		}

		// Create a timeline to animate all blocks
		const blocksTimeline = gsap.timeline({ paused: true });

		// Adding animations for each block
		// Gradient reveal animation for all devices
		blocks.forEach(function(block, index) {
			// Each block appears on its own part of the progress
			const startProgress = index / totalBlocks;
			const endProgress = (index + 1) / totalBlocks;
			const blockDuration = endProgress - startProgress;

			// Animate gradient reveal from left to right
			const title = block.querySelector('.features-section__block-title');
			const description = block.querySelector('.features-section__block-description');

			// Animate gradient stop from 0% to 100% over the block's duration
			// Title and description animate simultaneously
			// When gradient reaches 100%, remove gradient and show normal white text
			if (title && description) {
				// Create a proxy object to animate the gradient stop value
				const gradientProxy = { value: 0 };
				// Track if gradient has been removed to avoid repeated operations
				let gradientRemoved = false;

				blocksTimeline.to(gradientProxy, {
					value: 100,
					duration: blockDuration,
					ease: 'power2.out',
					onUpdate: function() {
						const currentValue = gradientProxy.value;
						const stopValue = Math.round(currentValue) + '%';

						// Apply gradient during animation
						if (currentValue < 100) {
							// Ensure gradient class is applied
							if (!title.classList.contains('gradient-reveal')) {
								title.classList.add('gradient-reveal');
							}
							if (!description.classList.contains('gradient-reveal')) {
								description.classList.add('gradient-reveal');
							}
							gradientRemoved = false;
						}

						title.style.setProperty('--gradient-stop', stopValue);
						description.style.setProperty('--gradient-stop', stopValue);

						// When gradient reaches 100%, remove it and show normal text
						if (currentValue >= 99.9 && !gradientRemoved) {
							gradientRemoved = true;

							// Remove gradient styles and show normal white text
							title.classList.remove('gradient-reveal');
							title.style.removeProperty('--gradient-stop');
							title.style.removeProperty('background');
							title.style.removeProperty('background-clip');
							title.style.removeProperty('-webkit-background-clip');
							title.style.removeProperty('-webkit-text-fill-color');
							title.style.color = '#fff';

							description.classList.remove('gradient-reveal');
							description.style.removeProperty('--gradient-stop');
							description.style.removeProperty('background');
							description.style.removeProperty('background-clip');
							description.style.removeProperty('-webkit-background-clip');
							description.style.removeProperty('-webkit-text-fill-color');
							description.style.color = '#fff';
						} else if (currentValue < 99.9 && gradientRemoved) {
							// If scrolling back, restore gradient
							gradientRemoved = false;
							title.classList.add('gradient-reveal');
							description.classList.add('gradient-reveal');
						}
					}
				}, startProgress);
			} else if (title) {
				const gradientProxy = { value: 0 };
				let gradientRemoved = false;

				blocksTimeline.to(gradientProxy, {
					value: 100,
					duration: blockDuration,
					ease: 'power2.out',
					onUpdate: function() {
						const currentValue = gradientProxy.value;
						const stopValue = Math.round(currentValue) + '%';

						if (currentValue < 100) {
							if (!title.classList.contains('gradient-reveal')) {
								title.classList.add('gradient-reveal');
							}
							gradientRemoved = false;
						}

						title.style.setProperty('--gradient-stop', stopValue);

						if (currentValue >= 99.9 && !gradientRemoved) {
							gradientRemoved = true;
							title.classList.remove('gradient-reveal');
							title.style.removeProperty('--gradient-stop');
							title.style.removeProperty('background');
							title.style.removeProperty('background-clip');
							title.style.removeProperty('-webkit-background-clip');
							title.style.removeProperty('-webkit-text-fill-color');
							title.style.color = '#fff';
						} else if (currentValue < 99.9 && gradientRemoved) {
							gradientRemoved = false;
							title.classList.add('gradient-reveal');
						}
					}
				}, startProgress);
			} else if (description) {
				const gradientProxy = { value: 0 };
				let gradientRemoved = false;

				blocksTimeline.to(gradientProxy, {
					value: 100,
					duration: blockDuration,
					ease: 'power2.out',
					onUpdate: function() {
						const currentValue = gradientProxy.value;
						const stopValue = Math.round(currentValue) + '%';

						if (currentValue < 100) {
							if (!description.classList.contains('gradient-reveal')) {
								description.classList.add('gradient-reveal');
							}
							gradientRemoved = false;
						}

						description.style.setProperty('--gradient-stop', stopValue);

						if (currentValue >= 99.9 && !gradientRemoved) {
							gradientRemoved = true;
							description.classList.remove('gradient-reveal');
							description.style.removeProperty('--gradient-stop');
							description.style.removeProperty('background');
							description.style.removeProperty('background-clip');
							description.style.removeProperty('-webkit-background-clip');
							description.style.removeProperty('-webkit-text-fill-color');
							description.style.color = '#fff';
						} else if (currentValue < 99.9 && gradientRemoved) {
							gradientRemoved = false;
							description.classList.add('gradient-reveal');
						}
					}
				}, startProgress);
			}
		});

		/**
		 * Progress line widths by position (px) for layout 1728.
		 * The layout line container is 1350px.
		 * Position 0: left edge of the container.
		 * Position 1: line center in the center of the container, left/right 350px.
		 * Position 2: right edge near the container.
		 */
		const progressLineWidths = [430, 700, 600];
		const designLineContainerWidth = 1350;

		/**
		 * Calculates the position and size of the progress line for a block.
		 * On mobile: vertical positioning with fixed block height + padding.
		 * On desktop: horizontal positioning with scalable width.
		 */
		function getProgressLinePosition(blockIndex) {
			if (blockIndex < 0 || blockIndex >= blockWrappers.length) {
				return isMobile()
					? { top: 0, height: 0, left: 0, width: 0 }
					: { left: 0, width: 0 };
			}

			// Mobile positioning: vertical line on the left
			if (isMobile()) {
				const activeBlockWrapper = blockWrappers[blockIndex];

				if (!activeBlockWrapper) {
					return { top: 0, height: 0, left: 0, width: 2 };
				}

				// Get block wrapper position relative to blocks wrapper
				const blocksWrapperRect = blocksWrapper.getBoundingClientRect();
				const blockWrapperRect = activeBlockWrapper.getBoundingClientRect();

				// Calculate top position relative to blocks wrapper
				// Add 14px padding on top: start 14px above the block
				const top = (blockWrapperRect.top - blocksWrapperRect.top) - 14;

				// Calculate height: use block-wrapper height + 14px top + 14px bottom padding
				// The height should match the block-wrapper height + 28px total padding
				const height = blockWrapperRect.height + 28; // 14px top + 14px bottom

				return {
					top: top + 'px',
					height: height + 'px',
					left: 0,
					width: 2
				};
			}

			// Desktop positioning: horizontal line at the bottom
			const wrapperRect = blocksWrapper.getBoundingClientRect();
			const baseWidth = progressLineWidths[blockIndex];
			const scale = wrapperRect.width / designLineContainerWidth;
			const width = baseWidth * scale;
			let left;

			if (blockIndex === 0) {
				// Position 0: starting point left, line at the left edge of the container
				left = 0;
			} else if (blockIndex === 1) {
				// Position 1: line centered, middle centered, left/right 350px (half of 700)
				left = wrapperRect.width / 2 - width / 2;
			} else {
				// Position 2: the right edge of the line abuts the container
				left = wrapperRect.width - width;
			}

			return { left: left, width: width };
		}

		// We save positions for each block
		let blockPositions = [];
		function updateBlockPositions() {
			blockPositions = [];
			blocks.forEach(function(blockWrapper, index) {
				blockPositions[index] = getProgressLinePosition(index);
			});
		}

		// Initializing positions
		updateBlockPositions();

		// Creating an animation for the progress line
		let progressLineAnimation = null;
		let currentBlockIndex = -1;

		// Progress line should appear simultaneously with the first block
		// Use a very small threshold to show it as soon as any progress starts
		const minProgressThreshold = 0.01; // Show progress line immediately when scrolling starts

		// Create a ScrollTrigger to fix a section and control animation
		// End includes exit band: model/header/blocks exit, then pin ends
		const scrollEndWithExit = isMobile() ? '+=1200vh' : '+=2200vh';
		const EXIT_START = 0.88; // exit phase starts at 88% of scroll
		const heroSectionImageOnly = document.querySelector('#hero-section[data-hero-image-only="true"]');

		const scrollTrigger = (ScrollTriggerPlugin.create || ScrollTriggerPlugin)({
			trigger: featuresSection,
			start: 'top top',
			end: scrollEndWithExit,
			pin: true,
			scrub: 1,
			anticipatePin: 1,
			refreshPriority: 1,
			onEnter: function() {
				setTimeout(function() {
					updateBlockPositions();
				}, 100);
			},
			onUpdate: function(self) {
				const progress = self.progress;
				const mainProgress = Math.min(progress, EXIT_START);
				// Blocks and header use mainProgress so they finish before exit phase
				const blocksProgress = mainProgress < headerProgressThreshold
					? 0
					: (mainProgress - headerProgressThreshold) / (EXIT_START - headerProgressThreshold);
				blocksTimeline.progress(blocksProgress);

				// Header text types in one continuous pass.
				applyHeaderTypeProgress(mainProgress);

				// Exit phase: model down, header up, blocks down (and fade out so not visible on next section), overlay fade (scrub-friendly)
				const vhPx = window.innerHeight || 800;
				if (progress < EXIT_START) {
					gsap.set(headerEl, { y: 0 });
					gsap.set(blocksWrapper, { y: 0, opacity: 1, visibility: 'visible', pointerEvents: 'auto' });
					// Image-only: hero.js keeps placeholder Y from end-of-hero slide; do not reset to 0 each frame
					if (!heroSectionImageOnly && typeof window.evonaHeroSetExitProgress === 'function') {
						window.evonaHeroSetExitProgress(0);
					}
				} else {
					const exitProgress = (progress - EXIT_START) / (1 - EXIT_START);
					gsap.set(headerEl, { y: -exitProgress * vhPx });
					gsap.set(blocksWrapper, {
						y: exitProgress * vhPx,
						opacity: 1 - exitProgress,
						visibility: exitProgress >= 1 ? 'hidden' : 'visible',
						pointerEvents: exitProgress >= 1 ? 'none' : 'auto'
					});
					if (typeof window.evonaHeroSetExitProgress === 'function') {
						window.evonaHeroSetExitProgress(exitProgress);
					}
				}

				// Calculate the current active block based on blocks progress
				const targetBlockIndex = blocksProgress > 0
					? Math.min(Math.floor(blocksProgress * totalBlocks), totalBlocks - 1)
					: -1;

				// Hide progress line only when blocks progress is 0 or very close to 0
				if (blocksProgress < minProgressThreshold) {
					if (currentBlockIndex !== -1) {
						currentBlockIndex = -1;

						// Removing all position classes
						progressLine.classList.remove('position-0', 'position-1', 'position-2');

						// Stop the previous animation if there is one
						if (progressLineAnimation) {
							progressLineAnimation.kill();
						}

						// Animating the hiding of the progress line
						progressLineAnimation = gsap.to(progressLine, {
							opacity: 0,
							scaleX: 0,
							scaleY: 0,
							duration: 0.5,
							ease: 'power2.in'
						});
					}
				} else if (targetBlockIndex >= 0) {
					// Show progress line for the current block
					// Update position if the block has changed or if it's the first time showing
					if (targetBlockIndex !== currentBlockIndex || currentBlockIndex === -1) {
						currentBlockIndex = targetBlockIndex;

						// We recalculate the position in real time
						const pos = getProgressLinePosition(targetBlockIndex);

						// Removing all position classes
						progressLine.classList.remove('position-0', 'position-1', 'position-2');
						// Add a class for the current position (for the gradient on desktop)
						progressLine.classList.add('position-' + targetBlockIndex);

						// Stop the previous animation if there is one
						if (progressLineAnimation) {
							progressLineAnimation.kill();
						}

						// Creating a new animation for the position and size of the line
						if (isMobile()) {
							// Mobile: vertical positioning from top to bottom
							// Only set initial position if this is the first block (currentBlockIndex === -1)
							if (currentBlockIndex === -1) {
								gsap.set(progressLine, {
									top: '0px',
									left: '0px',
									width: '2px',
									bottom: 'auto'
								});
							}

							// Animate smoothly from current position to new position
							progressLineAnimation = gsap.to(progressLine, {
								top: pos.top,
								height: pos.height,
								left: pos.left + 'px',
								width: pos.width + 'px',
								opacity: 1,
								scaleX: 1,
								scaleY: 1,
								duration: 0.8,
								ease: 'power2.out'
							});
						} else {
							// Desktop: horizontal positioning
							// Reset mobile styles first
							gsap.set(progressLine, {
								top: 'auto',
								height: '2px',
								bottom: '-24px'
							});

							progressLineAnimation = gsap.to(progressLine, {
								left: pos.left + 'px',
								width: pos.width + 'px',
								opacity: 1,
								scaleX: 1,
								scaleY: 1,
								duration: 0.8,
								ease: 'power2.out'
							});
						}
					}
				}
			}
		});

		// Initial header type and exit (e.g. page loaded already scrolled).
		applyHeaderTypeProgress(Math.min(scrollTrigger.progress, EXIT_START));
		(function () {
			const p = scrollTrigger.progress;
			const vh = window.innerHeight || 800;
			if (p < EXIT_START) {
				gsap.set(headerEl, { y: 0 });
				gsap.set(blocksWrapper, { y: 0, opacity: 1, visibility: 'visible', pointerEvents: 'auto' });
				if (typeof window.evonaHeroSetExitProgress === 'function') window.evonaHeroSetExitProgress(0);
			} else {
				const exitProgress = (p - EXIT_START) / (1 - EXIT_START);
				gsap.set(headerEl, { y: -exitProgress * vh });
				gsap.set(blocksWrapper, {
					y: exitProgress * vh,
					opacity: 1 - exitProgress,
					visibility: exitProgress >= 1 ? 'hidden' : 'visible',
					pointerEvents: exitProgress >= 1 ? 'none' : 'auto'
				});
				if (typeof window.evonaHeroSetExitProgress === 'function') window.evonaHeroSetExitProgress(exitProgress);
			}
		})();

		// Updating positions and ScrollTrigger when the window is resized
		let resizeTimer;
		let featuresResizeLastInnerWidth = window.innerWidth;
		window.addEventListener('resize', function() {
			var rw = window.innerWidth;
			if (rw <= 767 && rw === featuresResizeLastInnerWidth) {
				return;
			}
			featuresResizeLastInnerWidth = rw;
			clearTimeout(resizeTimer);
			resizeTimer = setTimeout(function() {
				// Recalculating the positions of blocks
				updateBlockPositions();

				// Update progress line position if a block is active
				if (currentBlockIndex >= 0) {
					const pos = getProgressLinePosition(currentBlockIndex);
					if (progressLineAnimation) {
						progressLineAnimation.kill();
					}

					if (isMobile()) {
						// Mobile: vertical positioning from top to bottom
						// Animate smoothly from current position to new position
						progressLineAnimation = gsap.to(progressLine, {
							top: pos.top,
							height: pos.height,
							left: pos.left + 'px',
							width: pos.width + 'px',
							opacity: 1,
							scaleX: 1,
							scaleY: 1,
							duration: 0.3,
							ease: 'power2.out'
						});
					} else {
						// Desktop: horizontal positioning
						// Reset mobile styles
						gsap.set(progressLine, {
							top: 'auto',
							height: '2px',
							bottom: '-24px'
						});

						progressLineAnimation = gsap.to(progressLine, {
							left: pos.left + 'px',
							width: pos.width + 'px',
							opacity: 1,
							scaleX: 1,
							scaleY: 1,
							duration: 0.3,
							ease: 'power2.out'
						});
					}
				}

				// Updating ScrollTrigger
				ScrollTriggerPlugin.refresh();
			}, 250);
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initFeaturesAnimation);
	} else {
		initFeaturesAnimation();
	}

})();
